import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button, Stack } from 'src/core/ui';
import { useConfirmationDialogSettings } from 'src/hooks/api/useConfirmationDialogSettings';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ConfirmationDialogSettingItem,
  ConfirmationDialogType,
} from 'src/WebApiController';

import { DialogSuppressionFormContent } from './DialogSuppressionFormContent';

interface DialogSuppressionFormProps {
  onDirtyStateChange: (isDirty: boolean) => void;
  isFormDirty: boolean;
}

export const DialogSuppressionForm = ({
  onDirtyStateChange,
  isFormDirty,
}: DialogSuppressionFormProps) => {
  const {
    confirmationDialogSettings,
    updateConfirmationDialogSettings,
    isLoading,
    isPending,
  } = useConfirmationDialogSettings();

  const formMethods = useForm<
    Record<ConfirmationDialogType, ConfirmationDialogSettingItem>
  >({
    defaultValues: confirmationDialogSettings?.settings || {},
  });

  const { reset, getValues, formState } = formMethods;

  useEffect(() => {
    if (confirmationDialogSettings?.settings) {
      reset(confirmationDialogSettings.settings);
    }
  }, [confirmationDialogSettings, reset]);

  useEffect(() => {
    onDirtyStateChange(formState.isDirty);
  }, [formState.isDirty, onDirtyStateChange]);

  const handleSave = useCallback(() => {
    const currentValues = getValues();
    updateConfirmationDialogSettings({ settings: currentValues });
  }, [getValues, updateConfirmationDialogSettings]);

  const handleCancel = useCallback(() => {
    if (confirmationDialogSettings?.settings) {
      reset(confirmationDialogSettings.settings);
    }
  }, [confirmationDialogSettings, reset]);

  if (isLoading || !confirmationDialogSettings) {
    return <PosSpinner />;
  }

  return (
    <FormProvider {...formMethods}>
      <Stack direction="column" gap="l" style={{ flex: 1 }}>
        <DialogSuppressionFormContent
          metadata={confirmationDialogSettings.metadata}
        />

        <div style={{ marginTop: 'auto' }}>
          {isFormDirty && (
            <Stack direction="row" gap="m" justifyContent="end">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isPending}
              >
                <Content id={ContentId.Cancel} />
              </Button>
              <Button
                variant="regular"
                onClick={handleSave}
                disabled={isPending}
              >
                <Content id={ContentId.Save} />
              </Button>
            </Stack>
          )}
        </div>
      </Stack>
    </FormProvider>
  );
};
